import React, { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import axiosApi from "../../utilities/axiosApi";

/**
 * Which approved WhatsApp template this campaign will send with.
 *
 * Only shown when the client is set to send from our official number. On that
 * path the message the customer typed is not what arrives: WhatsApp only
 * carries wording it has approved in advance, so the template's body is the
 * message. That is surprising enough to say out loud here rather than let
 * someone discover it from what their guests received.
 *
 * Choosing nothing is allowed only when the fallback is unambiguous: the
 * customer's own most recently approved template, or the single shared one.
 * Among several shared templates the API refuses to pick, because they belong
 * to whoever we imported them from and guessing has already meant resolving one
 * client's campaign to another family's invitation. The select says so rather
 * than offering a default that will be turned down at the end.
 */
export default function TemplatePicker({
  senderType,
  templateType,
  value,
  onChange,
  // Guest-list columns this campaign has, and the answer being built: which
  // column fills each {{n}} of the chosen template.
  columns = [],
  variableMap,
  onVariableMapChange,
}) {
  const [templates, setTemplates] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const official = senderType === "official";

  useEffect(() => {
    if (!official) return undefined;

    let active = true;
    setLoaded(false);

    axiosApi
      .get("/api/customer/whatsapp-templates/selectable", {
        params: { template_type: templateType || "text" },
      })
      .then((res) => {
        if (active) setTemplates(res?.data?.success ? res.data.data || [] : []);
      })
      .catch(() => {
        if (active) setTemplates([]);
      })
      .finally(() => {
        if (active) setLoaded(true);
      });

    return () => {
      active = false;
    };
  }, [official, templateType]);

  if (!official) {
    return null;
  }

  const selected = templates.find((t) => t.id === value);

  // Mirrors SenderSelection::defaultTemplate. A customer's own templates are
  // all theirs, so the newest is a fair default; shared ones are not.
  const hasOwn = templates.some((t) => t.customer_id);
  const mustChoose = loaded && !hasOwn && templates.length > 1;

  const map = variableMap || {};
  const blanks = selected?.placeholder_count || 0;

  // `name` is the guest's own name and is available whatever columns the
  // campaign declares, so it is always offered.
  const choices = ["name", ...columns.filter((c) => c && c !== "name")];

  const unanswered = Array.from({ length: blanks }, (_, i) => i + 1).filter(
    (position) => !map[String(position)]
  );

  const setPosition = (position, column) => {
    const next = { ...map };

    if (column) {
      next[String(position)] = column;
    } else {
      delete next[String(position)];
    }

    onVariableMapChange(next);
  };

  return (
    <div className="bg-white rounded-2 common-shadow p-4 mb-4">
      <h6 className="mb-1">WhatsApp template</h6>
      <p className="text-muted small mb-3">
        This client sends from our official WhatsApp number, so guests receive
        the approved template wording below — not the message typed above. Your
        message is still used for the PDF caption and test sends.
      </p>

      {loaded && templates.length === 0 ? (
        <div className="alert alert-warning mb-0">
          No approved template matches this campaign yet. Create one under
          WhatsApp Templates, or switch this client back to your own number.
          <div className="small mt-2 mb-0">
            Templates with a button that needs a different link for each guest
            are not listed &mdash; we cannot fill one in yet.
          </div>
        </div>
      ) : (
        <>
          <Form.Select
            value={value || ""}
            onChange={(e) => {
              onChange(e.target.value);
              // Positions mean different things in different templates, so a
              // map carried over from the last one would be worse than none.
              onVariableMapChange({});
            }}
            disabled={!loaded}
          >
            <option value="">
              {!loaded
                ? "Loading…"
                : mustChoose
                ? "Choose a template…"
                : "Use my most recently approved template"}
            </option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.display_name || t.name}
                {t.customer_id ? "" : " (shared)"}
              </option>
            ))}
          </Form.Select>

          {mustChoose && !selected && (
            <p className="text-muted small mb-0 mt-2">
              Pick the one this campaign should send. There is more than one
              approved template, and we will not choose for you — your guests
              would receive whichever it happened to land on.
            </p>
          )}

          {selected && (
            <div className="border rounded-2 p-3 mt-3 bg-light">
              <div className="small text-muted mb-1">Guests will receive:</div>
              <div style={{ whiteSpace: "pre-wrap" }}>{selected.body_text}</div>
            </div>
          )}

          {/* One answer per blank in the wording above. Asked here, per
              campaign, rather than stored on the template: the same approved
              template is offered to every customer, and {{1}} is a guest's
              name in one and a registration link in another. */}
          {selected && blanks > 0 && (
            <div className="mt-3">
              <h6 className="mb-1">Fill in the blanks</h6>
              <p className="text-muted small mb-3">
                The wording above has {blanks === 1 ? "a blank" : `${blanks} blanks`}.
                Choose which column from your guest list goes in each — every
                guest gets their own value.
              </p>

              {Array.from({ length: blanks }, (_, i) => i + 1).map((position) => (
                <div
                  key={position}
                  className="d-flex align-items-center gap-3 mb-2 flex-wrap"
                >
                  <code className="flex-shrink-0">{`{{${position}}}`}</code>
                  <Form.Select
                    value={map[String(position)] || ""}
                    onChange={(e) => setPosition(position, e.target.value)}
                    style={{ maxWidth: 260 }}
                  >
                    <option value="">Choose a column…</option>
                    {choices.map((column) => (
                      <option key={column} value={column}>
                        {column === "name" ? "name (the guest's name)" : column}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              ))}

              {unanswered.length > 0 && (
                <p className="text-danger small mb-0">
                  Still to fill in:{" "}
                  {unanswered.map((p) => `{{${p}}}`).join(", ")}. The campaign
                  cannot be sent until every blank has a column.
                </p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
